import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import swal from 'sweetalert';
import { format as formatDate } from 'date-fns';
import { useImmer } from 'use-immer';
import { immerable } from 'immer';
import _ from 'lodash';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import errorReporter from '@app/errorReporter';
import Swal from 'sweetalert2';
import axios from 'axios';
import { HashRouter, Route, useHistory } from 'react-router-dom';
import {
  isAssumeOrgType,
  getStartOfDayInTimezone,
  featureEnabled,
  isManualCoopUserWithAccess,
} from 'utils';
import { BuyerAgreementContext } from 'contexts';
import { message, Text, BuyerAgreementCheckbox } from 'components';
import { usePrevious, useMixpanel } from 'hooks';
import { MixpanelPage } from 'hooks/useMixpanel/events';
import { UserController } from '../../../../api';
import VirtualTourTab from './virtual_tour/VirtualTourTab';
import styles from './appointment_book.module.scss';
import AppointmentHeader from './AppointmentHeader';
import Step1User from './Step1User';
import Step23AppointmentBooking from './Step23AppointmentBooking';

import VtAppointmentBooking from './virtual_tour/VtAppointmentBooking';

import Step4Mobile from './Step4Mobile';
import MobileButtons from './MobileButtons';
import MainContainer from './MainContainer';
import {
  AppointmentOverride,
  getShowingScreenType,
  ShowingScreenType,
} from './AppointmentOverride';
import AppointmentSubHeader from './AppointmentSubHeader';
import InaccessibleListingPanelWrapper from './InaccessibleListingPanelWrapper';
import { HOME_INSPECTION_TYPES } from '../../constants';
import PreBookingInfoAlert from './PreBookingInfoAlert';

// The appointment booking page was split into an internal and external component since they are very different booking flows
// Not all internal logic has been extracted out of this component yet so you may see some duplicated or unused code
// If possible, try to remove dead code and/or DRY up duplicated code
const AppointmentBookingExternal = props => {
  // props passed in through the apointmentbooking.module.js
  // extAuth and apointmentDetails were part of the bindings
  const {
    fetchSavedAppointment,
    extAuth,
    setExtAuth,
    appointmentDetails,
    isListingContact,
    $location,
    $rootScope,
    $routeParams,
    $anchorScroll,
    ErrorService,
    AppointmentBookService,
    listingNotificationSettings,
    UtilService,
    ModalService,
  } = props;

  // hook to use Immer for easy immutable state change
  const [state, updateState] = useImmer({
    appointment: {},
    interval: 15,
    duration: 30,
    hours: [0, 1, 2, 3, 4, 5, 6],
    minutes: [0, 15, 30, 45],
    customDuration: {
      enabled: false,
      durationMinutes: 0,
      durationHour: 1,
    },
    enableOpenHouses: false,
    customContact: null,
    customContactSaved: false,
    nonAgent: {},
    customAgent: {},
    showCaptcha: false,
    nextStepLabel: 'Next >',
    appointmentTypes: [
      'Buyer/Broker',
      'Buyer Visit',
      'Agent Inspection',
      'Revisit',
      ...(featureEnabled('transactionBrokerTypeFeature')
        ? ['Transaction Broker']
        : []),
      'Dual Agency',
      'Home Inspection',
      'Appraisal',
      'Photographer',
      'Pest Control',
      ...HOME_INSPECTION_TYPES,
      'Information Request',
      'Final Walkthrough',
    ],
    nonAgentTypes: [
      'Appraisal',
      'Home Inspection',
      'Photographer',
      'Pest Control',
    ],
    naTypeMap: {
      Appraisal: 'Appraiser',
      ...HOME_INSPECTION_TYPES.reduce(
        (accum, type) => ({ ...accum, [type]: type }),
        {},
      ),
      'Home Inspection': 'Home Inspector',
      Photographer: 'Photographer',
      'Pest Control': 'Pest Control',
      'Buyer/Broker': 'Buyer/Broker',
      'Buyer Visit': 'Buyer',
      'Agent Inspection': `Agent Inspector${
        featureEnabled('agentInspectionRelabelFeature') ? '/Previewer' : ''
      }`,
      Revisit: 'Revisit',
      'Dual Agency': 'Dual Agency',
    },
    mobileWorkflowSteps: {
      initialStep: 0,
      selectDateStep: 1,
      selectTimeStep: 2,
      confirmDetailsStep: 3,
    },
    selectedTimes: {},
    invitees: [],
    keepOriginalAppointment: true,
    canBookAppointment: true,
    customContactPreApproved: false,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [partialAutoConfirm, setPartialAutoConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMobileNumber, setHasMobileNumber] = useState(true);
  const [prevDate, setPrevDate] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [showingOverrideType, setShowingOverrideType] = useState(null);
  const [isBookingAllowed, setIsBookingAllowed] = useState(true);
  const prevShowingOverrideType = usePrevious(showingOverrideType);
  const history = useHistory();

  const [inviteBuyers, setInviteBuyers] = useState([]);

  const { hasBuyerAgreed, showBuyerAgreementModal } = useContext(
    BuyerAgreementContext,
  );
  /** @type [{ hasNarBuyerSettlement?: boolean; loading: boolean }] */
  const [narSettlementData, setNarSettlementData] = useState({ loading: true });

  const [isFirstTimeFiringCovidModal, setIsFirstTimeFiringCovidModal] =
    useState(true);

  useMixpanel({
    pageTracking: { pageName: MixpanelPage.BookShowingExternalDashboard },
  });

  async function fireCOVIDModal() {
    setIsFirstTimeFiringCovidModal(false);
    const {
      data: { covid19PopupTemplate, publicHealthAgencySite },
    } = await axios.get(`/external/covid19/settings`, {
      params: {
        retsId: extAuth.retsId,
        dashboardId: extAuth.dashboardId,
        country: extAuth.listing.country,
      },
    });
    if (covid19PopupTemplate) {
      const { dismiss } = await Swal.fire({
        html: covid19PopupTemplate,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: ' I AGREE',
        cancelButtonText: 'I DO NOT AGREE',
        reverseButtons: true,
        width: 600,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (dismiss === 'cancel') {
        window.location.href = publicHealthAgencySite;
      }
    }
  }

  /**
   * @param {import('@bb/domain/dist/types').OrgFeatures} feature
   * @returns boolean
   */
  const hasFeature = feature =>
    state.listing?.org.features.includes(feature) ?? false;

  useEffect(() => {
    // checks if auth exist through org field
    if (!$rootScope.auth?.org) return;

    const isSameOrAnsweringOrg =
      ($rootScope.auth.org._id &&
        $rootScope.auth.org._id === state?.listing?.org._id) ||
      isAssumeOrgType($rootScope.auth.org.type);

    let canBookAppointment;
    if (!isSameOrAnsweringOrg) {
      const currentUserOrgMlsType = $rootScope.auth.org.extendedMlsTypes;
      const isSameMlsType = !!state?.listing?.org.mlsTypes.some(mlsType =>
        currentUserOrgMlsType.includes(mlsType),
      );
      // users from a different org and a different province can't book appointment
      canBookAppointment =
        isSameMlsType ||
        isManualCoopUserWithAccess($rootScope.auth, state.listing?.org);
    } else {
      canBookAppointment = true;
    }
    updateState(draft => ({ ...draft, canBookAppointment }));
  }, [$rootScope.auth, state?.listing]);

  useEffect(
    () => () => {
      Swal.close();
      swal.close();
    },
    [history],
  );

  useEffect(() => {
    if (
      isFirstTimeFiringCovidModal &&
      isBookingAllowed &&
      !(isListingContact && appointmentDetails) &&
      extAuth.listing.org.features.includes('COVID_19')
    )
      fireCOVIDModal();
  }, [isBookingAllowed]);

  useEffect(() => {
    let bookingInfoPromise;

    const isMobile = window.screen.width <= 767;
    if (isMobile) {
      setCurrentStep(state.mobileWorkflowSteps.initialStep);
    }
    updateState(draft => {
      draft.isMobile = isMobile;
      if (isMobile) setCurrentStep(state.mobileWorkflowSteps.selectDateStep);
      draft.isMls = true;
      draft.mobileContentHeight = `${window.innerHeight - 62}px`;
      draft.bookFor = 'self';
    });

    // If existing appointment ie. timechange
    if (appointmentDetails) {
      const appointment = _.clone(appointmentDetails.appointment);
      appointment[immerable] = true;
      updateState(draft => {
        draft.listing = appointmentDetails.listing;
        draft.listingId = appointmentDetails.listing.id;
        draft.appointment = appointment;
        draft.originalAppointment = appointmentDetails.appointment;
        draft.originalDate = draft.originalAppointment.time;
        draft.isTimeChange = true;
      });
      updateDate(appointmentDetails.appointment.date);
    } else {
      resetAppointment();
    }

    const existingListing = extAuth.listing || appointmentDetails.listing;

    if (existingListing && featureEnabled('narSettlementFeature')) {
      // Duplicate code here to use parallel API data fetching when possible
      const {
        org: { id: orgId, onboardingPortalId: portalId },
      } = existingListing;
      if (!portalId || !orgId) {
        throw new Error(
          `Cannot fetch NAR Settlement Values. Received portalId: ${portalId}, orgId: ${orgId}`,
        );
      }
    }

    if (extAuth.retsDashboard) {
      const {
        id: listingId,
        availability,
        timezone,
        org: { id: orgId, onboardingPortalId: portalId },
      } = existingListing;
      bookingInfoPromise = AppointmentBookService.getRetsAppointmentBookingInfo(
        extAuth.retsId,
        listingId,
        {
          orgId,
          portalId,
          availability,
          timezone,
        },
      );
    } else if (extAuth.coopDashboard || extAuth.externalDashboard) {
      bookingInfoPromise = AppointmentBookService.getDashAppointmentBookingInfo(
        extAuth.dashboardId,
        existingListing?.id || appointmentDetails?.listing.id,
        {
          orgId: existingListing?.org.id,
          portalId: existingListing?.org.onboardingPortalId,
          availability: existingListing?.availability,
          timezone: existingListing?.timezone,
          ...(!existingListing && { fetchListing: true }),
        },
      );
    }

    // !Logic Get Resources
    bookingInfoPromise
      .then(
        ({
          unavailableDays: unavailable,
          externalConfig,
          offerDetails: details,
          notificationSettings: notifications,
          listing: _listing = existingListing,
          serverDate,
          portalAssociationData: _associationData,
        }) => {
          let listing;
          if (Object.prototype.hasOwnProperty.call(_listing, 'listing')) {
            listing = _listing.listing;
          } else {
            listing = _listing;
          }

          if (notifications) {
            // eslint-disable-next-line no-param-reassign
            notifications[immerable] = true;
          }
          updateState(draft => {
            draft.enableOpenHouses = listing.org?.settings?.enableOpenHouses;
          });
          updateState(draft => {
            draft.listing = listing;
            if (!listing.timezone) draft.listing.timezone = 'America/Toronto';

            draft.unavailableDays = unavailable.days;
            if (unavailable.after) {
              draft.unavailableAfter = new Date(unavailable.after);
            }
            draft.appointmentConfig = externalConfig;
            draft.isVirtualTourEnabled = externalConfig.isVirtualTourEnabled;
            draft.noMoreShowings =
              externalConfig.noMoreShowings &&
              (!!draft.auth || !isListingContact);
            draft.duration = externalConfig.maxDuration
              ? externalConfig.maxDuration
              : 60;
            if (featureEnabled('autoConfirmFeature')) {
              draft.autoConfirm = externalConfig.autoConfirm;
              draft.advAutoConfEnabled = externalConfig.advAutoConfEnabled;
              draft.aptConfigDays = externalConfig.days;
              draft.aptConfigSpecialTimes = externalConfig.specialTimes;
            }
            draft.maxDuration = draft.duration;
            draft.openHouseDays = draft.listing.openHouses.map(({ timeFrom }) =>
              formatDate(new Date(timeFrom), 'yyyy-MM-dd'),
            );
            draft.offerAnytime = true;
            if (details?.offerSettings.acceptedAnytime !== undefined) {
              draft.offerAnytime = details.offerSettings.acceptedAnytime;
            }
            if (details?.offerSettings.offerDate) {
              const offerDate = moment(details.offerSettings.offerDate);
              offerDate.minute(
                Math.round(offerDate.minute() / draft.interval) *
                  draft.interval,
              );
              draft.offerDate = offerDate;
            }

            draft.notifications = notifications;

            if (serverDate) {
              draft.serverDate = serverDate;
            }
          });

          if (state.isMobile) {
            moveToStep(state.mobileWorkflowSteps.selectDateStep);
          }

          if (featureEnabled('narSettlementFeature')) {
            setNarSettlementData({
              loading: false,
              hasNarBuyerSettlement: Boolean(
                _associationData?.narSettlementAgreement,
              ),
            });
          } else {
            setNarSettlementData({ loading: false });
          }

          setLoading(false);
        },
      )
      .catch(err => {
        // TOAST
        errorReporter.error(err);
      })
      .finally(() => {
        updateState(draft => {
          draft.dataLoaded = true;
          draft.showComingSoonWarning = false;
        });
      });
  }, []);

  useEffect(() => {
    if (state.savedAppointment) fetchSavedAppointment(state.savedAppointment);
  }, [state.savedAppointment]);

  useEffect(() => {
    if (state.dataLoaded && state.earliestAvailableTime && state.shouldScroll) {
      scrollToEarliestAvailableTime(state.earliestAvailableTime);
      updateState(draft => {
        draft.shouldScroll = false;
      });
    }
  }, [state.dataLoaded, state.earliestAvailableTime, state.shouldScroll]);

  useEffect(() => {
    if (
      state.advAutoConfEnabled &&
      (state.isAdmin || state.listing.canEdit || state.listing.canRead) &&
      state.selectedTimes &&
      Object.keys(state.selectedTimes).length
    )
      setPartialAutoConfirm(
        checkPartialAutoConfirm(
          state.aptConfigDays,
          state.aptConfigSpecialTimes,
          state.selectedTimes,
          state.selecteddate,
        ),
      );
  }, [state.selectedTimes]);

  // *functions from appointment_book.component.js
  function appointmentInfo(dateStr) {
    if (extAuth.retsDashboard) {
      return AppointmentBookService.getRetsAppointmentInfo(
        extAuth.retsId,
        'times',
        dateStr,
      );
    }

    if (extAuth.coopDashboard || extAuth.externalDashboard) {
      return AppointmentBookService.getDashAppointmentInfo(
        extAuth.dashboardId,
        'times',
        appointmentDetails?.listing.id,
        dateStr,
      );
    }

    return AppointmentBookService.getAppointmentInfo(
      state.listingId,
      'times',
      dateStr,
    );
  }

  // *functions
  function resetDate() {
    const comingSoonDate =
      state.listing.status === 'Coming Soon' &&
      getStartOfDayInTimezone(
        state.listing.coming_soon_date,
        state.listing.timezone,
      );
    updateState(draft => {
      const initialDate = comingSoonDate || new Date();
      draft.selecteddate = initialDate;
      draft.localdate = initialDate;
      draft.currentViewDate = initialDate;
      draft.appointment.recordAppointment = false;
    });
  }

  const populateSelfAppointment = user => {
    updateState(draft => {
      draft.appointment.coop = {
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.emails?.[0] || user.email || '',
        emails: user.emails,
        phone: user.phone,
        text: user.text,
        board: user.board || '',
        boardId: user.boardId,
      };
      if (user.id) draft.appointment.coop.id = user.id;
      if (user.uid) draft.appointment.coop.uid = user.uid;
      draft.coopUserOrg = {
        name: user.org.name,
        address: user.org.address,
        phone: user.org.phone,
      };
      draft.sameOrg = user.org.id && user.org.id === extAuth.listing.org.id;
    });
  };

  /**
   * @param { 'text' | 'email' | 'email&text' | 'office' } option
   */
  const selectNotifyMethod = option =>
    updateState(draft => {
      draft.appointment.notify.email = false;
      draft.appointment.notify.text = false;
      draft.appointment.notify.office = false;
      if (option === 'email&text') {
        draft.appointment.notify.email = true;
        draft.appointment.notify.text = true;
      } else {
        draft.appointment.notify[option] = true;
      }
    });

  function resetAppointment() {
    if (extAuth.retsDashboard) {
      updateState(draft => {
        draft.appointment = {
          type:
            draft.bookFor === 'nonAgent'
              ? draft.nonAgentTypes[0]
              : draft.appointmentTypes[0],
          uid: extAuth.uid,
          retsId: extAuth.retsId,
          notify: {
            text: false,
            email: true,
            office: false,
          },
        };
      });
      populateSelfAppointment(extAuth.coop);
    } else {
      updateState(draft => {
        draft.appointment = {
          dashboardId: extAuth.dashboardId,
          type:
            draft.bookFor === 'nonAgent'
              ? draft.nonAgentTypes[0]
              : draft.appointmentTypes[0],
          notify: {
            text: false,
            email: true,
            office: false,
          },
        };
      });

      populateSelfAppointment(extAuth.user);
    }
  }

  // The verify human is bypassed on QA and  set to submitApointment
  const verifyHuman =
    $routeParams.bypassCaptcha === 'true' || window.Cypress
      ? submitAppointment
      : () => {
          updateState(draft => {
            draft.showCaptcha = true;
          });
        };

  function externalBookAppointment(captchaResponse, captchaV3Token) {
    const appointment = {
      ...state.appointment,
      captchaResponse,
      captchaV3Token,
    };

    updateState(draft => {
      if (draft.isMobile) {
        draft.showLoadingSpinnerInButton = true;
      } else {
        draft.dataLoaded = false;
      }
    });

    let saveAppointmentPromises;
    if (extAuth.retsDashboard) {
      saveAppointmentPromises = Promise.all([
        AppointmentBookService.saveRetsAppointment(appointment),
        listingNotificationSettings.save(extAuth.retsId, state.notifications),
      ]);
    } else if (extAuth.coopDashboard) {
      saveAppointmentPromises = Promise.all([
        AppointmentBookService.saveDashAppointment(appointment),
        listingNotificationSettings.saveDash(
          extAuth.dashboardId,
          state.notifications,
        ),
      ]);
    }

    if (saveAppointmentPromises) {
      saveAppointmentPromises
        .then(([res]) => {
          const { showChallenge } = res;
          const savedAppointment = res.result;

          if (showChallenge) {
            updateState(draft => {
              draft.showCaptcha = true;
              draft.dataLoaded = true;
            });
            setIsSubmitting(false);
            return null;
          }

          if (state.isMobile) {
            updateState(draft => {
              draft.showLoadingSpinnerInButton = false;
            });
          }

          message.success({ content: 'Showing booked' });

          return savedAppointment;
        })
        .then(savedAppointment => {
          if (savedAppointment) {
            updateState(draft => {
              draft.savedAppointment = {
                ...savedAppointment,
                country: draft.listing.country,
              };
              draft.dataLoaded = true;
            });
          }
        })
        .catch(err => {
          updateState(draft => {
            draft.showLoadingSpinnerInButton = false;
            draft.dataLoaded = true;
          });

          if (
            err.status === 403 &&
            err.data.message?.includes(
              'Oops, you can’t book a showing on this listing as it has been updated. Please book a showing on the new listing. URL: ',
            )
          ) {
            const hyperlinkURL = err.data.message.split('URL: ')[1];
            message.warning({
              content: (
                <Text>
                  Oops, you can’t book a showing on this listing as it has been
                  updated. Please{' '}
                  <a href={hyperlinkURL}>book a showing on the new listing.</a>
                </Text>
              ),
              style: { zIndex: '10' },
            });
            return;
          }

          ErrorService.toastyHTTPErr(err);
          $rootScope.$apply();
        })
        .finally(() => setIsSubmitting(false));

      return true;
    }

    return false;
  }

  function bookAppointment(
    confirm,
    { timeRestrictionsOverridden, captchaResponse, captchaV3Token } = {},
  ) {
    updateState(draft => {
      if (draft.isMobile) {
        draft.showLoadingSpinnerInButton = true;
      } else {
        draft.dataLoaded = false;
      }
    });

    let appointmentTimeChangePromise;
    let redirectAfterTimeChange;
    if (state.isTimeChange) {
      appointmentTimeChangePromise = AppointmentBookService.changeTimeExternal(
        { ...state.appointment, captchaResponse, captchaV3Token },
        extAuth.dashboardId,
        {
          timeRestrictionsOverridden,
          isListingContact,
          keepOriginalAppointment: state.keepOriginalAppointment,
        },
      );
      redirectAfterTimeChange = () => {
        $location.path(
          `/dash/${extAuth.dashboardId}/${
            extAuth.coopDashboard ? 'showing' : 'appointments'
          }/${state.appointment.id}`,
        );
      };

      appointmentTimeChangePromise
        .then(res => {
          const { showChallenge } = res;

          if (showChallenge) {
            updateState(draft => {
              draft.showCaptcha = true;
              draft.dataLoaded = true;
            });
            setIsSubmitting(false);
            return { showChallenge };
          }

          if (state.isMobile) {
            updateState(draft => {
              draft.showLoadingSpinnerInButton = false;
            });

            return new Promise(resolve => {
              swal(
                {
                  title: 'Time Changed',
                  text: 'The appointment time has been changed.',
                  type: 'success',
                },
                resolve,
              );
            });
          }
        })
        .then(res => {
          if (!res?.showChallenge) return redirectAfterTimeChange();
        })
        .catch(err => {
          updateState(draft => {
            draft.showLoadingSpinnerInButton = false;
            draft.dataLoaded = true;
          });

          ErrorService.toastyHTTPErr(err);
        })
        .finally(() => setIsSubmitting(false));
      return;
    }
    const tempAppointment = _.cloneDeep(state.appointment);
    if (state.customContact) {
      tempAppointment.customContact = state.customContact;
      tempAppointment.notify = {
        text: false,
        email: false,
        office: true,
      };
      updateState(draft => {
        draft.appointment.customContact = draft.customContact;
        draft.appointment.notify = {
          text: false,
          email: false,
          office: true,
        };
      });
    }

    AppointmentBookService.saveAppointment(
      {
        ...tempAppointment,
        buyers: inviteBuyers.map(buyer => buyer.value),
      },
      timeRestrictionsOverridden,
    )
      .then(savedAppointment => {
        if (state.isMobile) {
          updateState(draft => {
            draft.showLoadingSpinnerInButton = false;
          });
        }

        message.success({ content: 'Showing booked' });

        return savedAppointment;
      })
      .then(apt => {
        updateState(draft => {
          draft.savedAppointment = { ...apt, country: draft.listing.country };
          draft.dataLoaded = true;
        });
      })
      .catch(err => {
        updateState(draft => {
          draft.showLoadingSpinnerInButton = false;
          draft.dataLoaded = true;
        });

        if (
          err.status === 403 &&
          err.data.message?.includes(
            'Oops, you can’t book a showing on this listing as it has been updated. Please book a showing on the new listing. URL: ',
          )
        ) {
          const hyperlinkURL = err.data.message.split('URL: ')[1];
          message.warning({
            content: (
              <Text>
                Oops, you can’t book a showing on this listing as it has been
                updated. Please{' '}
                <a href={hyperlinkURL}>book a showing on the new listing.</a>
              </Text>
            ),
            style: { zIndex: '10' },
          });
          return;
        }

        ErrorService.toastyHTTPErr(err);
      })
      .finally(() => setIsSubmitting(false));
  }

  const isResidentialTypeListing = ['condo', 'freehold', 'income'].includes(
    state.listing?.type,
  );

  const isBuyerShowing =
    ['Buyer/Broker', 'Buyer Visit', 'Revisit', 'Dual Agency'].includes(
      state.appointment.type,
    ) &&
    (state.appointment.coop ||
      (!_.isEmpty(state.customAgent) && state.customContactSaved));

  const shouldSendModal =
    featureEnabled('narBuyerSettlementFeature') &&
    narSettlementData.hasNarBuyerSettlement &&
    !state.isTimeChange &&
    isBuyerShowing &&
    isResidentialTypeListing &&
    !hasBuyerAgreed;

  function submitAppointment(
    confirm,
    captchaResponse,
    captchaV3Token,
    // should be only used when submitAppointment is called recursively
    bypassBuyerAgreement,
  ) {
    setIsSubmitting(true);

    if (shouldSendModal && !bypassBuyerAgreement) {
      showBuyerAgreementModal({
        onOk: () =>
          submitAppointment(confirm, captchaResponse, captchaV3Token, true),
      });
      setIsSubmitting(false);
      return;
    }

    if (!state.isTimeChange) {
      const booking = externalBookAppointment(captchaResponse, captchaV3Token);
      if (booking) {
        if (setExtAuth) {
          setExtAuth(auth => ({ ...auth, hasBookedShowing: true }));
        }
        return;
      }
    }

    if (state.invalidTimeslot && (state.isAdmin || state.isListingAgent)) {
      swal(
        {
          title: 'Restricted time slot',
          text: 'You have selected a restricted time slot. Are you sure?',
          html: true,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          closeButtonText: 'Cancel',
          closeOnConfirm: true,
        },
        () => {
          bookAppointment(confirm, { timeRestrictionsOverridden: true });
        },
      );
      setIsSubmitting(false);
    } else {
      bookAppointment(confirm, { captchaV3Token, captchaResponse });
    }
  }

  const selectSearchRoster = () => {
    updateState(draft => {
      draft.customContact = null;
      draft.nonAgent = {};
      draft.customContactSaved = false;
      draft.appointment.type =
        draft.bookFor === 'nonAgent' ? null : draft.appointmentTypes[0];
      draft.nextStepLabel =
        draft.bookFor === 'nonAgent' || draft.bookFor === 'others'
          ? 'Save Profile'
          : 'Next >';
      draft.appointment.notify =
        draft.bookFor === 'nonAgent'
          ? {
              text: false,
              email: false,
              office: true,
            }
          : {
              text: false,
              email: true,
              office: false,
            };
    });
    resetDate();
  };

  // Agent/contact selection
  const deselectUser = () => {
    updateState(draft => {
      draft.selectedUser = null;
      draft.appointment.coop = null;
    });
    resetAppointment();
  };

  const selectUser = async user => {
    updateState(draft => {
      draft.appointment.coop = {};
      draft.nonAgent = {};
      draft.customContact = null;
    });

    resetDate();

    try {
      const foundUser = await UserController.searchDetails({
        ...user,
        reason: 'ViewingPreSale',
        listingId: state.listingId || state.listing?.id,
      });
      foundUser.uid ||= user.uid;
      foundUser.mlsType ||= user.mlsType;
      updateState(draft => {
        draft.selectedUser = foundUser;
        [draft.selectedEmail] = draft.selectedUser.emails;
        draft.appointment.coop = {
          uid: foundUser.uid,
          id: foundUser.id,
          email: foundUser.emails?.[0] || foundUser.email || '',
          name: `${foundUser.firstName} ${foundUser.lastName}`,
          firstName: foundUser.firstName,
          lastName: foundUser.lastName,
          text: foundUser.text,
          board: foundUser.board || '',
          boardId: foundUser.boardId,
          mls: foundUser.mlsType,
          orgFeatures: foundUser.org?.features,
        };
        draft.coopUserOrg = foundUser.org;
        if (user.id) {
          draft.appointment.coop.phone = foundUser.phone;
        }
        if (foundUser.lastPhone) {
          draft.appointment.coop.phone = foundUser.lastPhone;
        }
      });
    } catch (err) {
      // TOAST
      ErrorService.toastyHTTPErr(err);
    }
  };

  function timePassed(time) {
    const mins =
      Math.ceil(moment().minutes() / state.interval) * state.interval;
    return time.unix() < moment().minutes(mins).startOf('minute').unix();
  }

  const startRecord = () => {
    Swal.fire({
      title: 'RECORD SHOWING',
      text: "You've selected a date in the past, would you like to record a past showing?",
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
  };

  const startComingSoonBooking = () =>
    Swal.fire({
      title: 'BOOK SHOWING',
      text: `This listing will be active on ${formatDate(
        new Date(state.listing.coming_soon_date),
        'MMM d, yyyy',
      )}. Are you sure you'd like to book a showing before it goes live?`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

  useEffect(() => {
    if (state.listing?.openHouses) {
      updateDate(state.appointment.date);
    }
  }, [state.listing]);

  useEffect(() => {
    setInviteBuyers([]);
  }, [state.selectedUser]);

  function updateDate(date) {
    const isDayInPast = AppointmentBookService.isDayInPast(date);
    if (isDayInPast && (state.isAdmin || state.isListingAgent)) {
      if (state.showPastDateWarning) {
        startRecord().then(result => {
          if (result.value) {
            updateState(draft => {
              draft.appointment.recordAppointment = true;
              draft.selecteddate = date;
              draft.showPastDateWarning = false;
            });
            doUpdateDate(date);
          } else {
            resetDate();
          }
        });
      }

      updateState(draft => {
        draft.showPastDateWarning = true;
      });
    }

    const isComingSoonListing = state.listing?.status === 'Coming Soon';
    const isBeforeComingSoonDate = () =>
      date <
      getStartOfDayInTimezone(
        state.listing.coming_soon_date,
        state.listing.timezone,
      );

    if (
      !isDayInPast &&
      isComingSoonListing &&
      isBeforeComingSoonDate() &&
      (state.isAdmin || state.isListingAgent)
    ) {
      if (state.showComingSoonWarning) {
        startComingSoonBooking().then(result => {
          if (result.value) {
            updateState(draft => {
              draft.appointment.recordAppointment = false;
              draft.selecteddate = date;
              draft.showComingSoonWarning = false;
            });
            doUpdateDate(date);
          } else {
            resetDate();
          }
        });
      }

      updateState(draft => {
        draft.showComingSoonWarning = true;
      });
    }

    if (!isDayInPast) {
      updateState(draft => {
        draft.appointment.recordAppointment = false;
      });
    }

    doUpdateDate(date);
  }

  function doUpdateDate(date) {
    // Avoid updating the date if it's the same as the current date, or no appointment is available, or if undefined date is passed in
    if (
      (prevDate && moment(prevDate).isSame(date)) ||
      _.isEmpty(state.appointment) ||
      !date
    )
      return;

    setPrevDate(date);

    const dateStr = moment(date).format('YYYY-MM-DD');

    const startOfDay = moment
      .tz(dateStr, state.listing?.timezone)
      .startOf('day');
    const endOfDay = moment.tz(dateStr, state.listing?.timezone).endOf('day');
    const timeOfDay = startOfDay.clone();
    const timesOfDay = [];
    while (timeOfDay.unix() < endOfDay.unix()) {
      timesOfDay.push(timeOfDay.clone());
      timeOfDay.add(state.interval, 'm');
    }
    updateState(draft => {
      draft.timesOfDay = timesOfDay;
      draft.appointment.date = dateStr;
      draft.appointment.time = null;
      draft.selectedTimes = {};
      draft.invalidTimeslot = false;
    });

    if (
      (_.includes(state.unavailableDays, dateStr) &&
        !state.isAdmin &&
        !state.isListingAgent &&
        !state.isTeamAdmin) ||
      (state.unavailableAfter &&
        moment(date).unix() * 1000 >= state.unavailableAfter.getTime())
    ) {
      updateState(draft => {
        draft.dateNotAvailable = true;
      });
    } else {
      updateState(draft => {
        draft.dateNotAvailable = false;
        draft.loadingTimes = true;
      });
      if ((state.listing && state.listing.id) || state.listingId) {
        appointmentInfo(dateStr).then(data => {
          let earliestAvailableTime;
          updateState(draft => {
            draft.availableTimes = { ...data };
            draft.unavailableTimes = Object.keys(data).map(key => {
              if ({}.hasOwnProperty.call(data, key) && !data[key]) {
                return key;
              }
              const availableTime = moment(key, 'h:mm A');
              const dateTime = moment
                .tz(draft.appointment.date, draft.listing?.timezone)
                .hour(availableTime.hour())
                .minute(availableTime.minute());
              if (!earliestAvailableTime && !timePassed(dateTime))
                earliestAvailableTime = key;
              return undefined;
            });
            draft.earliestAvailableTime = earliestAvailableTime;
          });

          if (earliestAvailableTime) {
            scrollToEarliestAvailableTime(earliestAvailableTime);
          }
          updateState(draft => {
            draft.loadingTimes = false;
          });
        });
      }
    }
    updateState(draft => {
      delete draft.lastTimeStr;
      draft.appointmentTimes = {};
      draft.hiddenTimes = {};
      draft.appointmentsInfo = {};
      draft.openHouses = {};
      draft.openHouseTimes = {};
    });

    if (state.listing?.openHouses) {
      const openHouseTimes = {};
      for (let i = 0; i < state.listing.openHouses.length; i += 1) {
        const openHouse = state.listing.openHouses[i];
        const ohDate = moment(openHouse.timeFrom).format('YYYY-MM-DD');

        if (dateStr === ohDate) {
          const time = moment.tz(openHouse.timeFrom, state.listing.timezone);
          const timeEnd = moment.tz(openHouse.timeTo, state.listing.timezone);
          time.minute(
            Math.round(time.minute() / state.interval) * state.interval,
          );
          timeEnd.minute(
            Math.round(timeEnd.minute() / state.interval) * state.interval,
          );
          const addTime = time.clone();

          while (addTime.unix() <= timeEnd.unix()) {
            openHouseTimes[addTime.format('h:mm A')] = true;
            addTime.add(state.interval, 'm');
          }
          updateState(draft => {
            addTime.subtract(state.interval, 'm');
            draft.openHouses[addTime.format('h:mm A')] = true;
          });
        }
      }

      if (Object.keys(openHouseTimes).length) {
        updateState(draft => {
          draft.openHouseTimes = openHouseTimes;
        });
      }
    }
    return undefined;
  }

  function scrollToEarliestAvailableTime(earliestAvailableTime) {
    // Check data is loaded and view is rendered.
    /* If Data is not loaded
    useEffect will call scrollToearliest available time when data is loaded
    */
    if (state.dataLoaded) {
      const id = $location.hash();
      $location.hash(`timeWidget-${earliestAvailableTime}`);
      $anchorScroll();
      $location.hash(id);
    } else {
      updateState(draft => {
        draft.shouldScroll = true;
      });
    }
  }

  function setDuration(newDuration) {
    if (!state.appointment.time || !state.appointment.time.clone) {
      updateState(draft => {
        draft.invalidTimeslot = false;
      });
      return;
    }
    const time = state.appointment.time.clone();
    const timeEnd = time.clone().add(newDuration, 'm');

    const addTime = time.clone();
    const selectedTimes = {};
    while (addTime.unix() < timeEnd.unix()) {
      selectedTimes[addTime.format('h:mm A')] = true;
      addTime.add(state.interval, 'm');
    }
    updateState(draft => {
      draft.selectedTimes = selectedTimes;
      draft.appointment.timeEnd = timeEnd;
      draft.invalidTimeslot =
        _.intersection(draft.unavailableTimes, Object.keys(selectedTimes))
          .length > 0;
    });
  }

  const selectTime = time => {
    if (timePassed(time) && (state.isAdmin || state.isListingAgent)) {
      if (state.isTimeChange) {
        return;
      }
      if (!state.appointment.recordAppointment) {
        if (state.showPastDateWarning) {
          startRecord().then(result => {
            if (result.value) {
              updateState(draft => {
                draft.appointment.recordAppointment = true;
                draft.selecteddate = date;
                draft.showPastDateWarning = false;
              });
              doUpdateDate(date);
            } else {
              resetDate();
            }
          });
        }

        updateState(draft => {
          draft.showPastDateWarning = true;
        });
      }
    } else {
      updateState(draft => {
        draft.appointment.recordAppointment = false;
      });
    }
    updateState(draft => {
      draft.customDuration.enabled = false;
      draft.appointment.time = time;
    });
  };

  /**
   * @param {object} aptConfigDays - appointmentConfig.days {'FRI':{'11:00':'Available'}}
   * @param {object} aptConfigSpecialTimes - appointmentConfig.specialTimes {'2022-10-14':{'11:00':'Available'}}
   * @param {object} selectedTimes - selectedTimes {'10:00 AM': true}
   * @param {string} selectedDay - selectedDay 'FRI'
   * @returns {boolean} boolean
   */
  function checkPartialAutoConfirm(
    aptConfigDays,
    aptConfigSpecialTimes,
    selectedTimes,
    selectedDay,
  ) {
    const day = moment(selectedDay).format('ddd').toUpperCase();
    const specialDay = moment(selectedDay).format('YYYY-MM-DD');
    const selectedTimesArr = Object.keys(selectedTimes);

    // Rounding down to nearest 30 mins to match selectedtimes with the times in aptconfigdays
    const filteredArr = selectedTimesArr
      .map(timeslot => {
        const time = moment(timeslot, 'HH:mm a');
        if (time.format('mm') === '15' || time.format('mm') === '45') {
          time.subtract(15, 'm');
        }
        return time.format('H:mm');
      })
      .filter(
        time =>
          aptConfigDays[day][time] === 'AutoConfirm' ||
          aptConfigSpecialTimes?.[specialDay]?.[time] === 'AutoConfirm',
      );
    return selectedTimesArr.length === filteredArr.length;
  }

  function setCustomDuration() {
    const customDuration =
      Number(state.customDuration.durationHour) * 60 +
      Number(state.customDuration.durationMinutes);
    setDuration(customDuration);
  }

  function enableCustomDuration(customDuration) {
    updateState(draft => {
      draft.customDuration.enabled = customDuration;
    });
    if (customDuration) {
      setCustomDuration();
    } else {
      setDuration(state.duration);
    }
  }

  const expandTime = (event, timeStr, expand) => {
    const { timezone } = state.listing;
    if (expand) {
      setDuration(0);
      if (state.lastTimeStr) expandTime(event, state.lastTimeStr, false);
      updateState(draft => {
        draft.selectedTimes = {};
        draft.appointment.time = null;
        draft.lastTimeStr = timeStr;
      });
    }
    event.stopPropagation();
    let maxCount = 0;
    const startOfDay = moment
      .tz(state.appointment.date, timezone)
      .startOf('day');

    const endOfDay = moment.tz(state.appointment.date, timezone).endOf('day');
    let maxTimeEnd = startOfDay.clone();

    state.appointmentsInfo[timeStr].appointments.forEach(appointment => {
      const time = moment.tz(
        moment.max(moment(appointment.time), startOfDay),
        timezone,
      );
      const timeEnd = moment.tz(
        moment.min(moment(appointment.timeEnd), endOfDay),
        timezone,
      );
      const addTime = time.clone();
      let count = 1;
      addTime.add(state.interval, 'm');
      const hiddenTimes = {};
      while (addTime.unix() < timeEnd.unix()) {
        count += 1;
        hiddenTimes[addTime.format('h:mm A')] = expand;
        addTime.add(state.interval, 'm');
      }
      updateState(draft => {
        draft.hiddenTimes = hiddenTimes;
      });
      maxCount = Math.max(count, maxCount);
      maxTimeEnd = moment.tz(
        moment.max(maxTimeEnd, moment(appointment.timeEnd)),
        timezone,
      );
    });
    updateState(draft => {
      draft.appointmentsInfo[timeStr].expand = expand
        ? { 'min-height': `${maxCount * 43}px` }
        : undefined;
      draft.appointmentsInfo[timeStr].maxTimeEnd = maxTimeEnd;
    });
  };

  const getPrettyDate = date => moment(date).format('MMM DD, YYYY');

  const hasValidTime = () => {
    // These users should be able to book regardless of restrictions
    if (state.isAdmin || state.isListingAgent || state.isTeamAdmin) return true;

    // If the date is completely restricted from the weekly time selector,
    // `vm.dateNotAvailable` is true and we should disallow booking
    if (state.dateNotAvailable) return false;

    // Otherwise, we need to check availabilities defined by existing
    // appointments and the special day settings
    const result = Object.entries(state.availableTimes || { 0: true })
      .filter(([key]) => !key.startsWith('$')) // Filter out promise-related keys
      .some(([, isAvailable]) => isAvailable);
    return result;
  };

  function moveToStep(step) {
    if (step < 0) return clearSelected();
    setCurrentStep(step);
    if (step === state.mobileWorkflowSteps.selectTimeStep) {
      updateState(draft => {
        draft.shouldScroll = true;
      });
    }
  }

  const moveToNextStep = () => {
    moveToStep(currentStep + 1);
  };

  const moveToPreviousStep = () => {
    moveToStep(currentStep - 1);
  };

  const clearSelected = () => {
    if (showingOverrideType) return setShowingOverrideType(null);
    updateState(draft => {
      draft.selectedAppointment = null;
    });
  };

  const addPropertyToTour = async (
    listingId,
    virtualTourId,
    invitees,
    captchaResponse,
    captchaV3Token,
    bypassBuyerAgreement,
  ) => {
    if (shouldSendModal && !bypassBuyerAgreement) {
      showBuyerAgreementModal({
        onOk: () =>
          addPropertyToTour(
            listingId,
            virtualTourId,
            invitees,
            captchaResponse,
            captchaV3Token,
            true,
          ),
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `/external/virtual_tour/${virtualTourId}/property_to_tour/add`,
        {
          listingId,
          invitees,
          captchaResponse,
          captchaV3Token,
          retsId: extAuth.retsId,
          dashboardId: extAuth.dashboardId,
        },
      );
      if (data.showChallenge) {
        setIsSubmitting(false);
        return updateState(draft => {
          draft.showCaptcha = true;
        });
      }
      let path;
      if (extAuth.retsDashboard) {
        await axios.post(`/external/listing/${extAuth.retsId}/notifications`, {
          ...state.notifications,
        });
        path = $location
          .path()
          .replace(
            `appointments/book`,
            `tour/${state.selectedTour.id}/details`,
          );
      } else if (extAuth.coopDashboard) {
        await axios.post(
          `/external/dash/${extAuth.dashboardId}/notifications`,
          {
            ...state.notifications,
          },
        );
        path = $location
          .path()
          .replace(`/book`, `/tour/${state.selectedTour.id}/details`);
      }

      $rootScope.$apply(() => {
        $location.path(path);
      });
    } catch (err) {
      errorReporter.error(`Error adding property to virtual tour: ${err}`);
    }
  };

  const buildPage = () => (
    <>
      <AppointmentHeader
        appointment={state.appointment}
        auth={state.auth}
        autoConfirm={state.autoConfirm}
        extAuth={extAuth}
        invalidTimeslot={state.invalidTimeslot}
        isAdmin={state.isAdmin}
        isListingAgent={state.isListingAgent}
        isListingContact={isListingContact}
        isMls={state.isMls}
        isTeamAdmin={state.isTeamAdmin}
        isTimeChange={state.isTimeChange}
        isMobile={state.isMobile}
        isBookingVirtualTour={state.isBookingVirtualTour}
        selectedTour={state.selectedTour}
        invitees={state.invitees}
        listing={state.listing}
        listingId={state.listingId}
        savedAppointment={state.savedAppointment}
        showCaptcha={state.showCaptcha}
        showError={state.showError}
        orgDetails={state.orgDetails}
        notifications={state.notifications}
        registeredOffers={
          state.notifications && state.notifications.registeredOffers
        }
        partialAutoConfirm={partialAutoConfirm}
        isSubmitting={isSubmitting}
        customDuration={state.customDuration}
        showingOverrideType={showingOverrideType}
        // functions
        setIsSubmitting={setIsSubmitting}
        setHasMobileNumber={setHasMobileNumber}
        addPropertyToTour={addPropertyToTour}
        updateState={updateState}
        submitAppointment={submitAppointment}
        clearSelected={clearSelected}
        verifyHuman={verifyHuman}
        // angular/services
        errorReporter={errorReporter}
        ModalService={ModalService}
        $location={$location}
        $rootScope={$rootScope}
      />

      {!loading &&
        !narSettlementData.loading &&
        state.listing &&
        state.listing.id && (
          <MainContainer
            isMobile={state.isMobile}
            mobileContentHeight={state.mobileContentHeight}
          >
            <div className={`row ${styles.visibleBlockOnMobile}`}>
              <div className="col-xs-12">
                <h5>
                  <span>
                    <AppointmentSubHeader
                      isTimeChange={state.isTimeChange}
                      isListingContact={isListingContact}
                      isMobile={state.isMobile}
                    />
                  </span>
                </h5>
                <h3 className={styles.address}>
                  <strong>
                    {state.listing && state.listing.line1}{' '}
                    {state.listing && state.listing.line2}
                  </strong>
                </h3>
              </div>
            </div>

            {!showingOverrideType &&
            getShowingScreenType(
              state.listing?.canEdit,
              state.listing?.availability,
              state.noMoreShowings,
            ) !== ShowingScreenType.Default ? (
              <AppointmentOverride
                listing={state.listing}
                handleOverride={setShowingOverrideType}
                selectedOverrideType={prevShowingOverrideType}
                noMoreShowings={state.noMoreShowings}
                isExternal
                updateState={updateState}
                orgDetails={state.orgDetails}
                availability={
                  $rootScope.listingAvailabilityRelabel?.[
                    state.listing?.availability
                  ] || state.listing?.availability
                }
                setIsBookingAllowed={setIsBookingAllowed}
              />
            ) : (
              <div className={`row ${styles.flexGrow}`}>
                <div className="col-md-12">
                  <div className="row">
                    {state.isVirtualTourEnabled && !state.isTimeChange && (
                      <VirtualTourTab
                        isMobile={state.isMobile}
                        setCurrentStep={setCurrentStep}
                        updateState={updateState}
                        hasVSFeatureFlag={hasFeature('VIRTUAL_SHOWINGS')}
                      />
                    )}
                    <PreBookingInfoAlert state={state} />
                    <Step1User
                      {...state}
                      extAuth={extAuth}
                      isListingContact={isListingContact}
                      isUnverified={$rootScope.auth.isUnverified}
                      updateState={updateState}
                      currentStep={currentStep}
                      selectUser={selectUser}
                      deselectUser={deselectUser}
                      selectSearchRoster={selectSearchRoster}
                      selectNotifyMethod={selectNotifyMethod}
                      resetDate={resetDate}
                      $rootScope={$rootScope}
                      AppointmentBookService={AppointmentBookService}
                      ErrorService={ErrorService}
                      hasMobileNumber={hasMobileNumber}
                      setHasMobileNumber={setHasMobileNumber}
                      isLicensed={$rootScope.auth?.isLicensed}
                      isBookingVirtualTour={state.isBookingVirtualTour}
                      isListingRep={state.isListingRep}
                      showingOverrideType={showingOverrideType}
                      inviteBuyers={inviteBuyers}
                      setInviteBuyers={setInviteBuyers}
                    />

                    {/* Step 2 / 3 AptBooking or Virtual Tour */}
                    {((state.appointment && state.appointment.coop) ||
                      state.customContactSaved) && (
                      <>
                        {state.isBookingVirtualTour ? (
                          <VtAppointmentBooking
                            listingId={state.listing.id}
                            coop={
                              extAuth.coop ||
                              extAuth.user || {
                                ...state.appointment?.coop,
                                org: state.coopUserOrg,
                              }
                            }
                            currentStep={currentStep}
                            selectedTour={state.selectedTour}
                            updateState={updateState}
                            dashboardId={extAuth.dashboardId}
                            retsId={extAuth.retsId}
                          />
                        ) : (
                          <Step23AppointmentBooking
                            state={state}
                            extAuth={extAuth}
                            isListingContact={isListingContact}
                            currentStep={currentStep}
                            updateState={updateState}
                            updateDate={updateDate}
                            $rootScope={$rootScope}
                            UtilService={UtilService}
                            partialAutoConfirm={partialAutoConfirm}
                            selectTime={selectTime}
                            setDuration={setDuration}
                            moveToStep={moveToStep}
                            setCustomDuration={setCustomDuration}
                            enableCustomDuration={enableCustomDuration}
                            hasValidTime={hasValidTime}
                            getPrettyDate={getPrettyDate}
                            timePassed={timePassed}
                            expandTime={expandTime}
                          />
                        )}
                      </>
                    )}
                    {state.isMobile && (
                      <Step4Mobile
                        appointment={state.appointment}
                        listing={state.listing}
                        originalAppointment={state.originalAppointment}
                        keepOriginalAppointment={state.keepOriginalAppointment}
                        appointmentTypes={state.appointmentTypes}
                        bookFor={state.bookFor}
                        coopUserOrg={state.coopUserOrg}
                        currentStep={currentStep}
                        isMls={state.isMls}
                        isMobile={state.isMobile}
                        isListingContact={isListingContact}
                        isTimeChange={state.isTimeChange}
                        mobileWorkflowSteps={state.mobileWorkflowSteps}
                        notifications={state.notifications}
                        sameOrg={state.sameOrg}
                        selectedUser={state.selectedUser}
                        showCaptcha={state.showCaptcha}
                        updateState={updateState}
                        selectNotifyMethod={selectNotifyMethod}
                        moveToStep={moveToStep}
                        hasMobileNumber={hasMobileNumber}
                        setHasMobileNumber={setHasMobileNumber}
                        extAuth={extAuth}
                        showingOverrideType={showingOverrideType}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {
              // if mobile, only show on last stage
              // obs: last stage for virtual tours seems to be 2
              !(
                state.isMobile &&
                ((currentStep < 3 && !state.isBookingVirtualTour) ||
                  currentStep < 2)
              ) &&
                // Buyer Agreement is hidden for modify or suggested time change flows
                !state.isTimeChange &&
                isBuyerShowing &&
                isResidentialTypeListing &&
                isBookingAllowed &&
                narSettlementData.hasNarBuyerSettlement && (
                  <BuyerAgreementCheckbox />
                )
            }

            {state.isMobile && isBookingAllowed && (
              <MobileButtons
                isBookingVirtualTour={state.isBookingVirtualTour}
                noMoreShowings={state.noMoreShowings}
                isMls={state.isMls}
                showCaptcha={state.showCaptcha}
                currentStep={currentStep}
                mobileWorkflowSteps={state.mobileWorkflowSteps}
                extAuth={extAuth}
                listing={state.listing}
                appointment={state.appointment}
                customContactSaved={state.customContactSaved}
                dateNotAvailable={state.dateNotAvailable}
                isAdmin={state.isAdmin}
                isListingContact={isListingContact}
                isTimeChange={state.isTimeChange}
                isListingAgent={state.isListingAgent}
                invalidTimeslot={state.invalidTimeslot}
                showLoadingSpinnerInButton={state.showLoadingSpinnerInButton}
                selectedTour={state.selectedTour}
                invitees={state.invitees}
                isSubmitting={isSubmitting}
                showingOverrideType={showingOverrideType}
                // functions
                setIsSubmitting={setIsSubmitting}
                setHasMobileNumber={setHasMobileNumber}
                addPropertyToTour={addPropertyToTour}
                updateState={updateState}
                verifyHuman={verifyHuman}
                submitAppointment={submitAppointment}
                clearSelected={clearSelected}
                moveToNextStep={moveToNextStep}
                moveToPreviousStep={moveToPreviousStep}
              />
            )}
          </MainContainer>
        )}
    </>
  );

  return (
    <InaccessibleListingPanelWrapper
      dataLoaded={state.dataLoaded}
      listingFound={Boolean(state.listing)}
      canBookAppointment={state.canBookAppointment}
      listingVisibility={state.listing?.visibility}
      listingOrgFeatures={state.listing?.org?.features}
    >
      <GoogleReCaptchaProvider
        useEnterprise
        reCaptchaKey={process.env.RECAPTCHA_V3_SITE_KEY}
      >
        {buildPage()}
      </GoogleReCaptchaProvider>
    </InaccessibleListingPanelWrapper>
  );
};

export default props => (
  <HashRouter>
    <Route render={() => <AppointmentBookingExternal {...props} />} />
  </HashRouter>
);
